import { customFetch } from '@agos/api-client'
import type { FranchiseModulesResponse, FranchisePackage } from '@agos/api-client/model'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/**
 * Which features this franchise's SaaS tier includes, ported from legacy
 * `contexts/franchise-modules-context.tsx`.
 *
 * **It fails open, and that is the whole design.** When the call errors, or no
 * tenant resolved, or the module list comes back empty, every module counts as
 * enabled. A gating call that failed must never be the reason a restaurant
 * cannot reach its own till — §14.6: gating hides features, it does not take
 * the business offline.
 */

const MODULE_POLL_INTERVAL_MS = 60_000

interface FranchiseModulesContextValue {
  // Optional, to match the generated `FranchiseModulesResponse` this spreads. The schema marks every
  // field optional because it declares no `required` list; the endpoint itself always answers all
  // four, `franchiseId: null` included. Consumers already treat an absent tier as ungated.
  franchiseId?: number | null
  modules?: string[]
  package?: FranchisePackage | null
  theme?: string
  isLoading: boolean
  /** True when the tier is unknown — gating then answers yes to everything. */
  unknown: boolean
  isModuleEnabled: (key: string) => boolean
  refetch: () => Promise<void>
}

const FranchiseModulesContext = createContext<FranchiseModulesContextValue | null>(null)

// `undefined`, not `null`: the generated type marks every field optional, which is what an OpenAPI
// schema without a `required` list means. The endpoint does answer `franchiseId: null` — this is the
// local stand-in used before the first fetch resolves.
const NO_TIER: FranchiseModulesResponse = {
  franchiseId: undefined,
  modules: [],
  package: undefined,
  theme: 'classic',
}

export function FranchiseModulesProvider({ children }: { children: ReactNode }) {
  const [tier, setTier] = useState<FranchiseModulesResponse>(NO_TIER)
  const [isLoading, setIsLoading] = useState(true)
  const [unknown, setUnknown] = useState(true)

  const load = useCallback(async (showLoading = true) => {
    // The mount call and the 60 s poll pass false: the tier is already known
    // by then, and flipping the flag would announce a refresh nobody asked
    // to see.
    if (showLoading) setIsLoading(true)
    try {
      const payload = await customFetch<FranchiseModulesResponse>('/api/v1/franchise/modules')
      setTier(payload)
      // `== null` on purpose: the endpoint answers `franchiseId: null` when the request is not
      // bound to a franchise, and the generated type also allows `undefined` because the schema
      // marks it optional. Both mean the same thing here — nobody could be identified.
      setUnknown(payload.franchiseId == null || (payload.modules ?? []).length === 0)
    } catch {
      setUnknown(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // The rule cannot see that `load(false)` reaches no setState before its
    // first await, so it assumes the worst. Fetching the tier on mount is the
    // ported behaviour and there is nowhere earlier to do it.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(false)

    // A tier change happens in the Control Tower, on another domain, and there
    // is no push channel to the manager. The endpoint is cached 60 s
    // server-side, so this poll costs nothing.
    const poll = setInterval(() => {
      if (document.visibilityState === 'visible') void load(false)
    }, MODULE_POLL_INTERVAL_MS)

    const reloadWhenVisible = () => void load(false)
    window.addEventListener('focus', reloadWhenVisible)
    document.addEventListener('visibilitychange', reloadWhenVisible)

    return () => {
      clearInterval(poll)
      window.removeEventListener('focus', reloadWhenVisible)
      document.removeEventListener('visibilitychange', reloadWhenVisible)
    }
  }, [load])

  const enabledModules = useMemo(() => new Set(tier.modules ?? []), [tier.modules])

  const value = useMemo<FranchiseModulesContextValue>(
    () => ({
      ...tier,
      isLoading,
      unknown,
      isModuleEnabled: (key: string) => unknown || enabledModules.has(key),
      refetch: () => load(),
    }),
    [tier, isLoading, unknown, enabledModules, load],
  )

  return (
    <FranchiseModulesContext.Provider value={value}>{children}</FranchiseModulesContext.Provider>
  )
}

export function useFranchiseModules(): FranchiseModulesContextValue {
  const context = useContext(FranchiseModulesContext)
  if (!context) {
    throw new Error('useFranchiseModules must be used inside FranchiseModulesProvider')
  }
  return context
}
